
import React, { useState } from 'react';
import { Table, AppSettings, MenuItem } from '../types';
import { Plus, Trash2, Edit2, Check, X, LayoutTemplate, Image as ImageIcon, Palette, Coffee, Sun, Cloud, Umbrella, Monitor, Users, Star, Heart, Music, Leaf, Zap, Moon, Clock, AlertTriangle, Store, Upload, CalendarOff, CalendarCheck, Sparkles, MonitorSmartphone, Megaphone, ShieldCheck, Key, Lock, Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SwipeNumberInput } from './MobileControls';
import { db } from '../services/db';

interface SettingsViewProps {
  tables: Table[];
  menuItems: MenuItem[];
  appSettings: AppSettings;
  onUpdateSettings: (settings: AppSettings) => void;
  onUpdateTable: (table: Table) => void;
  onReorderTables: (tables: Table[]) => void;
  onAddTable: (name: string, capacity: number) => void;
  onDeleteTable: (id: string) => void;
}

export const SettingsView: React.FC<SettingsViewProps> = ({ tables, menuItems, appSettings, onUpdateSettings, onUpdateTable, onReorderTables, onAddTable, onDeleteTable }) => {
  const [editingTable, setEditingTable] = useState<Table | null>(null);
  const [newTableName, setNewTableName] = useState('');
  const [newTableCapacity, setNewTableCapacity] = useState(2);
  
  // Password Change State
  const [isChangingPass, setIsChangingPass] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passLoading, setPassLoading] = useState(false);

  const [irregularDate, setIrregularDate] = useState('');
  const [irregularOpenDate, setIrregularOpenDate] = useState('');

  const stopProp = (e: React.SyntheticEvent) => e.stopPropagation();

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTable && editingTable.name.trim()) {
      onUpdateTable(editingTable);
      setEditingTable(null);
    }
  };

  const moveTable = (index: number, direction: number) => {
    const newTables = [...tables];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= tables.length) return;
    
    [newTables[index], newTables[targetIndex]] = [newTables[targetIndex], newTables[index]];
    onReorderTables(newTables);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert('パスワードが一致しません。');
      return;
    }
    if (newPassword.length < 4) {
      alert('パスワードは4文字以上で設定してください。');
      return;
    }
    setPassLoading(true);
    try {
      await db.changePassword(newPassword);
      alert('パスワードを変更しました。');
      setNewPassword('');
      setConfirmPassword('');
      setIsChangingPass(false);
    } catch (e) {
      alert('変更に失敗しました。');
    } finally {
      setPassLoading(false);
    }
  };

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (newTableName.trim()) {
      onAddTable(newTableName, newTableCapacity);
      setNewTableName('');
      setNewTableCapacity(2);
    }
  };

  const updateStoreInfo = (key: keyof AppSettings, value: string) => {
    onUpdateSettings({
      ...appSettings,
      [key]: value
    });
  };

  const updateAutoOrder = (key: keyof NonNullable<AppSettings['autoOrderConfig']>, value: any) => {
     const currentConfig = appSettings.autoOrderConfig || { enabled: false, menuItemId: '' };
     onUpdateSettings({
       ...appSettings,
       autoOrderConfig: {
         ...currentConfig,
         [key]: value
       }
     });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, callback: (base64: string) => void) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        callback(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleClosedDay = (dayIndex: number) => {
    const currentClosed = appSettings.closedDays || [];
    let newClosed;
    if (currentClosed.includes(dayIndex)) {
      newClosed = currentClosed.filter(d => d !== dayIndex);
    } else {
      newClosed = [...currentClosed, dayIndex];
    }
    onUpdateSettings({ ...appSettings, closedDays: newClosed });
  };

  const addIrregularDate = () => {
    if (!irregularDate) return;
    const currentDates = appSettings.closedDates || [];
    if (!currentDates.includes(irregularDate)) {
      onUpdateSettings({ ...appSettings, closedDates: [...currentDates, irregularDate].sort() });
    }
    setIrregularDate('');
  };

  const removeIrregularDate = (dateStr: string) => {
    const currentDates = appSettings.closedDates || [];
    onUpdateSettings({ ...appSettings, closedDates: currentDates.filter(d => d !== dateStr) });
  };

  const addIrregularOpenDate = () => {
    if (!irregularOpenDate) return;
    const currentDates = appSettings.openDates || [];
    if (!currentDates.includes(irregularOpenDate)) {
      onUpdateSettings({ ...appSettings, openDates: [...currentDates, irregularOpenDate].sort() });
    }
    setIrregularOpenDate('');
  };

  const removeIrregularOpenDate = (dateStr: string) => {
    const currentDates = appSettings.openDates || [];
    onUpdateSettings({ ...appSettings, openDates: currentDates.filter(d => d !== dateStr) });
  };

  const DAYS_OF_WEEK = ['日', '月', '火', '水', '木', '金', '土'];

  // Input common classes for consistency
  const inputBaseClass = "w-full bg-white border border-stone-200 rounded-xl px-4 focus:outline-none focus:ring-2 focus:ring-stone-300 transition-all text-sm font-bold h-[52px]";

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-xl font-bold text-cafe-text">店舗設定</h2>
      </div>

      <div className="bg-white rounded-3xl p-6 shadow-sm border border-stone-100 flex-1 overflow-y-auto space-y-8 no-scrollbar">
        
        {/* Business Days Settings */}
        <div className="bg-red-50/50 border border-red-100 rounded-2xl p-6">
           <h3 className="font-bold text-red-800 mb-4 flex items-center gap-2">
             <CalendarOff size={20} />
             営業日・休日設定
           </h3>
           <p className="text-sm text-red-600/70 mb-4">
             定休日や臨時休業日を設定します。
           </p>

           <div className="space-y-6">
             <div>
                <label className="text-xs font-bold text-stone-500 block mb-2">定休曜日</label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map((day, index) => {
                    const isClosed = appSettings.closedDays?.includes(index);
                    return (
                      <button
                        key={day}
                        onClick={() => toggleClosedDay(index)}
                        className={`w-10 h-10 rounded-full font-bold transition-all ${
                          isClosed 
                            ? 'bg-red-500 text-white shadow-md' 
                            : 'bg-white border border-stone-200 text-stone-500 hover:bg-stone-50'
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs font-bold text-stone-500 block mb-2">臨時休業日（不定期）</label>
                  <div className="flex gap-2 mb-3">
                    <input 
                      type="date"
                      className="bg-white border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-red-200 w-full"
                      value={irregularDate}
                      onChange={(e) => setIrregularDate(e.target.value)}
                    />
                    <button onClick={addIrregularDate} className="bg-red-100 text-red-700 px-4 rounded-xl font-bold hover:bg-red-200 transition-colors whitespace-nowrap">追加</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {appSettings.closedDates?.map(date => (
                      <div key={date} className="bg-white border border-red-100 text-red-600 px-3 py-1 rounded-lg text-sm flex items-center gap-2 shadow-sm">
                          <span>{date}</span>
                          <button onClick={() => removeIrregularDate(date)} className="hover:text-red-800"><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-stone-500 mb-2 flex items-center gap-1">
                     <span className="w-2 h-2 rounded-full bg-green-500"></span>
                     臨時営業日
                  </label>
                  <div className="flex gap-2 mb-3">
                    <input 
                      type="date"
                      className="bg-white border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-200 w-full"
                      value={irregularOpenDate}
                      onChange={(e) => setIrregularOpenDate(e.target.value)}
                    />
                    <button onClick={addIrregularOpenDate} className="bg-green-100 text-green-700 px-4 rounded-xl font-bold hover:bg-green-200 transition-colors whitespace-nowrap">追加</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {appSettings.openDates?.map(date => (
                      <div key={date} className="bg-white border border-green-100 text-green-600 px-3 py-1 rounded-lg text-sm flex items-center gap-2 shadow-sm">
                          <span>{date}</span>
                          <button onClick={() => removeIrregularOpenDate(date)} className="hover:text-green-800"><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
             </div>
           </div>
        </div>
        
        {/* Store Info Settings Section */}
        <div className="bg-stone-100 border border-stone-200 rounded-2xl p-6">
           <h3 className="font-bold text-stone-700 mb-4 flex items-center gap-2">
             <Store size={20} />
             店舗情報・営業時間設定
           </h3>
           
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                 <label className="text-xs font-bold text-stone-500">店舗名</label>
                 <input 
                   type="text" 
                   className={inputBaseClass}
                   placeholder="例: KOMOREBI Cafe"
                   value={appSettings.storeName || ''}
                   onChange={(e) => updateStoreInfo('storeName', e.target.value)}
                 />
              </div>

              <div className="space-y-1">
                 <label className="text-xs font-bold text-stone-500">ロゴ (絵文字・URL・アップロード)</label>
                 <div className="flex gap-2">
                    <input 
                      type="text" 
                      className="flex-1 bg-white border border-stone-200 rounded-xl px-4 focus:outline-none focus:ring-2 focus:ring-stone-300 transition-all text-sm font-bold h-[52px]"
                      value={appSettings.storeLogo || ''}
                      onChange={(e) => updateStoreInfo('storeLogo', e.target.value)}
                    />
                    <label className="w-12 h-[52px] bg-white border border-stone-300 hover:bg-stone-50 rounded-xl flex items-center justify-center cursor-pointer">
                      <Upload size={20} className="text-stone-500" />
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, (base64) => updateStoreInfo('storeLogo', base64))} />
                    </label>
                 </div>
              </div>

              {/* Business Hours Settings */}
              <div className="space-y-1">
                 <label className="text-xs font-bold text-stone-500">開店時間 (予定表に反映)</label>
                 <input 
                   type="time" 
                   step="1800"
                   className={`${inputBaseClass} [color-scheme:light] appearance-none`}
                   value={appSettings.businessStartTime || '10:00'}
                   onChange={(e) => updateStoreInfo('businessStartTime', e.target.value)}
                 />
              </div>

              <div className="space-y-1">
                 <label className="text-xs font-bold text-stone-500">閉店時間 (予定表に反映)</label>
                 <input 
                   type="time" 
                   step="1800"
                   className={`${inputBaseClass} [color-scheme:light] appearance-none`}
                   value={appSettings.businessEndTime || '22:00'}
                   onChange={(e) => updateStoreInfo('businessEndTime', e.target.value)}
                 />
              </div>
           </div>
        </div>

        {/* Auto Order Settings */}
        <div className="bg-green-50/50 border border-green-100 rounded-2xl p-6">
           <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2">
             <Sparkles size={20} />
             自動注文設定
           </h3>
           <div className="flex items-center justify-between mb-4">
              <span className="font-bold text-stone-600 text-sm">自動注文を有効にする</span>
              <button onClick={() => updateAutoOrder('enabled', !appSettings.autoOrderConfig?.enabled)} className={`w-12 h-6 rounded-full transition-colors relative ${appSettings.autoOrderConfig?.enabled ? 'bg-green-500' : 'bg-stone-300'}`}>
                <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-all ${appSettings.autoOrderConfig?.enabled ? 'left-7' : 'left-1'}`} />
              </button>
           </div>
           {appSettings.autoOrderConfig?.enabled && (
             <select className="w-full bg-white border border-green-200 rounded-xl px-4 h-[52px] font-bold text-sm" value={appSettings.autoOrderConfig?.menuItemId || ''} onChange={(e) => updateAutoOrder('menuItemId', e.target.value)}>
               <option value="">選択してください</option>
               {menuItems.filter(i => i.isActive).map(item => <option key={item.id} value={item.id}>{item.name} (¥{item.price})</option>)}
             </select>
           )}
        </div>

        {/* Display Settings */}
        <div className="bg-stone-100 border border-stone-200 rounded-2xl p-6 space-y-4">
           <h3 className="font-bold text-stone-700 mb-2 flex items-center gap-2"><MonitorSmartphone size={20} />表示設定</h3>
           <div className="flex items-center justify-between">
              <span className="font-bold text-stone-600 text-sm">時計のリアルタイム更新</span>
              <button onClick={() => onUpdateSettings({ ...appSettings, enableRealtimeClock: !(appSettings.enableRealtimeClock ?? true) })} className={`w-12 h-6 rounded-full transition-colors relative ${appSettings.enableRealtimeClock ?? true ? 'bg-stone-800' : 'bg-stone-300'}`}><div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-all ${appSettings.enableRealtimeClock ?? true ? 'left-7' : 'left-1'}`} /></button>
           </div>
           <textarea className="w-full bg-white border border-stone-200 rounded-xl p-3 h-20 resize-none text-sm" placeholder="お知らせバナー..." value={appSettings.announcement || ''} onChange={(e) => onUpdateSettings({ ...appSettings, announcement: e.target.value })} />
        </div>

        {/* Table Settings Section */}
        <div className="bg-orange-50/50 border border-orange-100 rounded-2xl p-6">
          <h3 className="font-bold text-orange-800 mb-4 flex items-center gap-2"><LayoutTemplate size={20} />座席・テーブル設定</h3>
          
          <form onSubmit={handleAdd} className="space-y-3 mb-6 bg-white p-4 rounded-2xl border border-orange-100 shadow-sm">
            <div className="flex gap-2">
              <div className="flex-1 space-y-1">
                <label className="text-[10px] font-bold text-stone-400 ml-1 uppercase">Name</label>
                <input type="text" placeholder="テーブル名..." className="w-full bg-stone-50 border border-stone-100 rounded-xl px-4 h-12 text-sm" value={newTableName} onChange={(e) => setNewTableName(e.target.value)} />
              </div>
              <div className="w-32 space-y-1">
                <label className="text-[10px] font-bold text-stone-400 ml-1 uppercase">Capacity</label>
                <input type="number" min="1" className="w-full bg-stone-50 border border-stone-100 rounded-xl px-4 h-12 text-sm" value={newTableCapacity} onChange={(e) => setNewTableCapacity(parseInt(e.target.value) || 1)} />
              </div>
            </div>
            <button type="submit" disabled={!newTableName.trim()} className="w-full bg-orange-800 text-white h-12 rounded-xl font-bold disabled:opacity-50 transition-colors shadow-lg shadow-orange-900/10">新しいテーブルを追加</button>
          </form>

          <div className="grid grid-cols-1 gap-3">
            {tables.map((table, index) => (
              <div key={table.id} className="flex items-center justify-between p-4 bg-white rounded-xl border border-stone-100 shadow-sm" style={{ backgroundColor: table.backgroundColor }}>
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex flex-col gap-1 mr-2">
                    <button 
                      type="button"
                      disabled={index === 0} 
                      onClick={() => moveTable(index, -1)}
                      className={`p-1.5 rounded-lg transition-colors ${index === 0 ? 'text-stone-200' : 'text-stone-400 hover:bg-stone-100 hover:text-orange-500'}`}
                    >
                      <ChevronUp size={18} />
                    </button>
                    <button 
                      type="button"
                      disabled={index === tables.length - 1} 
                      onClick={() => moveTable(index, 1)}
                      className={`p-1.5 rounded-lg transition-colors ${index === tables.length - 1 ? 'text-stone-200' : 'text-stone-400 hover:bg-stone-100 hover:text-orange-500'}`}
                    >
                      <ChevronDown size={18} />
                    </button>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center font-bold border overflow-hidden shrink-0">{table.customIcon ? <img src={table.customIcon} className="w-full h-full object-cover" /> : table.name.charAt(0)}</div>
                  <div className="flex flex-col">
                    <span className="font-bold text-sm" style={{ color: table.textColor }}>{table.name}</span>
                    <span className="text-[10px] font-bold opacity-40 uppercase" style={{ color: table.textColor }}>Max {table.capacity} ppl</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditingTable(table)} className="p-2 bg-stone-50 text-stone-600 rounded-lg hover:bg-stone-200"><Edit2 size={16} /></button>
                  <button onClick={() => confirm(`${table.name} 削除？`) && onDeleteTable(table.id)} className="p-2 bg-stone-50 text-stone-400 hover:text-red-500 rounded-lg"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Account Security Settings (Moved to Bottom) */}
        <div className="bg-stone-800 text-white rounded-2xl p-6">
           <h3 className="font-bold mb-4 flex items-center gap-2">
             <ShieldCheck size={20} className="text-orange-400" />
             アカウントセキュリティ
           </h3>
           <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <span className="font-bold text-sm block">管理パスワード</span>
                <span className="text-[10px] text-stone-400 uppercase tracking-widest">ログインに使用するパスワード</span>
              </div>
              <button 
                onClick={() => setIsChangingPass(!isChangingPass)}
                className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-xl text-xs font-bold transition-all"
              >
                {isChangingPass ? <X size={16} /> : <Lock size={16} />}
              </button>
           </div>

           <AnimatePresence>
             {isChangingPass && (
               <motion.form 
                 initial={{ height: 0, opacity: 0 }}
                 animate={{ height: 'auto', opacity: 1 }}
                 exit={{ height: 0, opacity: 0 }}
                 onSubmit={handlePasswordChange}
                 className="mt-6 space-y-4 pt-4 border-t border-white/10 overflow-hidden"
               >
                 <div className="space-y-1">
                    <label className="text-[10px] font-bold text-stone-400 uppercase">新しいパスワード</label>
                    <input 
                      required
                      type="password" 
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                 </div>
                 <div className="space-y-1">
                    <label className="text-[10px] font-bold text-stone-400 uppercase">確認のため再入力</label>
                    <input 
                      required
                      type="password" 
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                 </div>
                 <button 
                  disabled={passLoading}
                  className="w-full bg-orange-400 text-stone-900 font-bold py-3 rounded-xl shadow-lg hover:bg-orange-300 transition-colors flex items-center justify-center gap-2"
                 >
                   {passLoading ? <Loader2 className="animate-spin" size={16} /> : <><Check size={16} /> パスワードを更新</>}
                 </button>
               </motion.form>
             )}
           </AnimatePresence>
        </div>

      </div>

      {/* Edit Table Modal */}
      <AnimatePresence>
        {editingTable && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-3xl w-full max-w-md p-6 overflow-hidden shadow-2xl">
              <h3 className="font-bold text-lg mb-4 text-center">テーブル編集: {editingTable.name}</h3>
              <form onSubmit={handleSaveEdit} className="space-y-4">
                <div className="space-y-1">
                   <label className="text-xs font-bold text-stone-500">テーブル名</label>
                   <input required className="w-full bg-stone-50 rounded-xl p-3 border-none focus:ring-2 focus:ring-stone-200 font-bold" value={editingTable.name} onChange={(e) => setEditingTable({ ...editingTable, name: e.target.value })} />
                </div>
                
                <div className="space-y-1">
                   <label className="text-xs font-bold text-stone-500">定員 (名)</label>
                   <input 
                     type="number" 
                     min="1" 
                     className="w-full bg-stone-50 rounded-xl p-3 border-none focus:ring-2 focus:ring-stone-200 font-bold" 
                     value={editingTable.capacity} 
                     onChange={(e) => setEditingTable({ ...editingTable, capacity: parseInt(e.target.value) || 1 })} 
                   />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1"><label className="text-xs font-bold text-stone-500">背景色</label><input type="color" className="w-full h-10 rounded-lg cursor-pointer" value={editingTable.backgroundColor || '#ffffff'} onChange={(e) => setEditingTable({ ...editingTable, backgroundColor: e.target.value })} /></div>
                  <div className="space-y-1"><label className="text-xs font-bold text-stone-500">文字色</label><input type="color" className="w-full h-10 rounded-lg cursor-pointer" value={editingTable.textColor || '#44403c'} onChange={(e) => setEditingTable({ ...editingTable, textColor: e.target.value })} /></div>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setEditingTable(null)} className="flex-1 text-stone-400 py-3 font-bold hover:bg-stone-50 rounded-xl">キャンセル</button>
                  <button type="submit" className="flex-[2] bg-stone-800 text-white font-bold py-3 rounded-xl shadow-lg">保存する</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
